import { Book, Bug, CheckCircle, ExternalLink, FileText, GraduationCap, Headphones, Mail, MessageCircle, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: string;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, initialSection = 'overview' }) => {
  const [activeSection, setActiveSection] = useState(initialSection);
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    priority: 'medium'
  });
  const [bugForm, setBugForm] = useState({
    title: '',
    severity: 'low',
    steps: '',
    expected: '',
    actual: '',
    browser: ''
  });
  const [submissionStatus, setSubmissionStatus] = useState<{
    type: 'contact' | 'bug' | null;
    status: 'idle' | 'submitting' | 'success' | 'error';
    message?: string;
  }>({ type: null, status: 'idle' });

  // Update active section when initialSection changes
  useEffect(() => {
    if (isOpen && initialSection) {
      setActiveSection(initialSection);
    }
  }, [isOpen, initialSection]);

  // Reset forms and status when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSubmissionStatus({ type: null, status: 'idle' });
    } else {
      // Reset forms when modal closes
      setContactForm({ subject: '', message: '', priority: 'medium' });
      setBugForm({ title: '', severity: 'low', steps: '', expected: '', actual: '', browser: '' });
      setSubmissionStatus({ type: null, status: 'idle' });
    }
  }, [isOpen]);

  const handleContactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      setSubmissionStatus({
        type: 'contact',
        status: 'error',
        message: 'Please fill in all required fields.'
      });
      return;
    }

    setSubmissionStatus({ type: 'contact', status: 'submitting' });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmissionStatus({
        type: 'contact',
        status: 'success',
        message: 'Your support request has been submitted successfully! We\'ll get back to you within 24 hours.'
      });
      
      // Reset form after successful submission
      setContactForm({ subject: '', message: '', priority: 'medium' });
    } catch (error) {
      setSubmissionStatus({
        type: 'contact',
        status: 'error',
        message: 'Failed to submit your request. Please try again.'
      });
    }
  };

  const handleBugFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!bugForm.title.trim() || !bugForm.steps.trim()) {
      setSubmissionStatus({
        type: 'bug',
        status: 'error',
        message: 'Please fill in the bug title and steps to reproduce.'
      });
      return;
    }

    setSubmissionStatus({ type: 'bug', status: 'submitting' });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmissionStatus({
        type: 'bug',
        status: 'success',
        message: 'Bug report submitted successfully! Our development team will investigate and get back to you.'
      });
      
      // Reset form after successful submission
      setBugForm({ title: '', severity: 'low', steps: '', expected: '', actual: '', browser: '' });
    } catch (error) {
      setSubmissionStatus({
        type: 'bug',
        status: 'error',
        message: 'Failed to submit bug report. Please try again.'
      });
    }
  };

  if (!isOpen) return null;

  const sections = [
    { id: 'overview', label: 'Overview', icon: Book },
    { id: 'documentation', label: 'Documentation', icon: FileText },
    { id: 'tutorials', label: 'Video Tutorials', icon: GraduationCap },
    { id: 'contact', label: 'Contact Support', icon: Headphones },
    { id: 'bug-report', label: 'Report a Bug', icon: Bug },
  ];

  const renderStatusMessage = (type: 'contact' | 'bug') => {
    if (submissionStatus.type !== type) return null;

    const { status, message } = submissionStatus;
    
    if (status === 'success') {
      return (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-green-900">Success!</h4>
              <p className="text-sm text-green-700 mt-1">{message}</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (status === 'error') {
      return (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <X className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-red-900">Error</h4>
              <p className="text-sm text-red-700 mt-1">{message}</p>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Welcome to HubSpot CRM Help Center</h3>
              <p className="text-gray-600 mb-6">
                Get the most out of your CRM with our comprehensive help resources. Whether you're new to the platform
                or looking to master advanced features, we've got you covered.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3 mb-3">
                  <FileText className="h-6 w-6 text-gray-600" />
                  <h4 className="font-medium text-gray-900">Documentation</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Complete guides and API references for all features
                </p>
                <button
                  onClick={() => setActiveSection('documentation')}
                  className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                >
                  Browse Documentation →
                </button>
              </div>

              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3 mb-3">
                  <GraduationCap className="h-6 w-6 text-gray-600" />
                  <h4 className="font-medium text-gray-900">Video Tutorials</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Step-by-step video guides for common workflows
                </p>
                <button
                  onClick={() => setActiveSection('tutorials')}
                  className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                >
                  Watch Tutorials →
                </button>
              </div>

              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3 mb-3">
                  <Headphones className="h-6 w-6 text-gray-600" />
                  <h4 className="font-medium text-gray-900">Contact Support</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Get personalized help from our support team
                </p>
                <button
                  onClick={() => setActiveSection('contact')}
                  className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                >
                  Contact Us →
                </button>
              </div>

              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3 mb-3">
                  <Bug className="h-6 w-6 text-gray-600" />
                  <h4 className="font-medium text-gray-900">Report Issues</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Found a bug? Let us know so we can fix it
                </p>
                <button
                  onClick={() => setActiveSection('bug-report')}
                  className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                >
                  Report Bug →
                </button>
              </div>
            </div>
          </div>
        );

      case 'documentation':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Documentation</h3>
              <p className="text-gray-600 mb-6">
                Comprehensive guides to help you make the most of HubSpot CRM.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { title: 'Getting Started Guide', description: 'Learn the basics of setting up and using HubSpot CRM', category: 'Basics', url: 'https://docs.example.com/getting-started' },
                { title: 'Lead Management', description: 'Complete guide to managing leads and opportunities', category: 'Sales', url: 'https://docs.example.com/leads' },
                { title: 'Contact Management', description: 'Organize and manage your contacts effectively', category: 'Contacts', url: 'https://docs.example.com/contacts' },
                { title: 'Activity Tracking', description: 'Track interactions and activities with your prospects', category: 'Activities', url: 'https://docs.example.com/activities' },
                { title: 'Reporting & Analytics', description: 'Generate insights with our reporting tools', category: 'Analytics', url: 'https://docs.example.com/analytics' },
                { title: 'API Documentation', description: 'Technical documentation for developers', category: 'Developer', url: 'https://api.example.com/docs' },
                { title: 'Integrations', description: 'Connect HubSpot CRM with other tools', category: 'Integrations', url: 'https://docs.example.com/integrations' },
                { title: 'Mobile App Guide', description: 'Using HubSpot CRM on mobile devices', category: 'Mobile', url: 'https://docs.example.com/mobile' },
              ].map((doc, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors cursor-pointer"
                  onClick={() => window.open(doc.url, '_blank')}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium text-gray-900">{doc.title}</h4>
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-800">
                          {doc.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{doc.description}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-gray-400 ml-3 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'tutorials':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Video Tutorials</h3>
              <p className="text-gray-600 mb-6">
                Watch step-by-step video guides to master HubSpot CRM features.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Getting Started with HubSpot CRM', duration: '5:30', views: '12.5K', category: 'Basics', url: 'https://youtube.com/watch?v=example1' },
                { title: 'Creating and Managing Leads', duration: '8:15', views: '9.2K', category: 'Sales', url: 'https://youtube.com/watch?v=example2' },
                { title: 'Setting Up Your Contact Database', duration: '6:45', views: '7.8K', category: 'Contacts', url: 'https://youtube.com/watch?v=example3' },
                { title: 'Using the Activity Feed', duration: '4:20', views: '6.1K', category: 'Activities', url: 'https://youtube.com/watch?v=example4' },
                { title: 'Generating Sales Reports', duration: '10:30', views: '5.4K', category: 'Analytics', url: 'https://youtube.com/watch?v=example5' },
                { title: 'Mobile App Overview', duration: '7:00', views: '4.2K', category: 'Mobile', url: 'https://youtube.com/watch?v=example6' },
              ].map((video, index) => (
                <div 
                  key={index} 
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => window.open(video.url, '_blank')}
                >
                    <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                    <div className="flex items-center justify-center h-40 bg-gradient-to-br from-teal-400 to-teal-600">
                      <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all">
                        <div className="w-0 h-0 border-l-[8px] border-r-0 border-b-[6px] border-t-[6px] border-l-white border-t-transparent border-b-transparent ml-1"></div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {video.category}
                      </span>
                      <span className="text-xs text-gray-500">{video.duration}</span>
                    </div>
                    <h4 className="font-medium text-gray-900 mb-1">{video.title}</h4>
                    <p className="text-xs text-gray-500">{video.views} views</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Support</h3>
              <p className="text-gray-600 mb-6">
                Need help? Our support team is here to assist you. Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </div>

            {renderStatusMessage('contact')}

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <MessageCircle className="h-5 w-5 text-teal-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-teal-900">Live Chat Available</h4>
                  <p className="text-sm text-teal-700 mt-1">
                    For immediate assistance, try our live chat feature available Monday-Friday, 9 AM - 6 PM EST.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleContactFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Brief description of your issue"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={contactForm.priority}
                  onChange={(e) => setContactForm(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={6}
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Please describe your issue in detail..."
                  required
                />
              </div>

              <div className="flex items-center space-x-4">
                <button
                  type="submit"
                  disabled={submissionStatus.type === 'contact' && submissionStatus.status === 'submitting'}
                  className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submissionStatus.type === 'contact' && submissionStatus.status === 'submitting' 
                    ? 'Sending...' 
                    : 'Send Message'
                  }
                </button>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span>We'll respond within 24 hours</span>
                </div>
              </div>
            </form>
          </div>
        );

      case 'bug-report':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Report a Bug</h3>
              <p className="text-gray-600 mb-6">
                Help us improve HubSpot CRM by reporting any bugs or issues you encounter. The more details you provide, the faster we can fix it.
              </p>
            </div>

            {renderStatusMessage('bug')}

            <form onSubmit={handleBugFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bug Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={bugForm.title}
                  onChange={(e) => setBugForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Brief description of the bug"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity
                </label>
                <select 
                  value={bugForm.severity}
                  onChange={(e) => setBugForm(prev => ({ ...prev, severity: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="low">Low - Minor issue</option>
                  <option value="medium">Medium - Affects functionality</option>
                  <option value="high">High - Major issue</option>
                  <option value="critical">Critical - System unusable</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Steps to Reproduce <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  value={bugForm.steps}
                  onChange={(e) => setBugForm(prev => ({ ...prev, steps: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Behavior
                </label>
                <textarea
                  rows={3}
                  value={bugForm.expected}
                  onChange={(e) => setBugForm(prev => ({ ...prev, expected: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="What should have happened?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Actual Behavior
                </label>
                <textarea
                  rows={3}
                  value={bugForm.actual}
                  onChange={(e) => setBugForm(prev => ({ ...prev, actual: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="What actually happened?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Browser & Device Info
                </label>
                <input
                  type="text"
                  value={bugForm.browser}
                  onChange={(e) => setBugForm(prev => ({ ...prev, browser: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Chrome 91.0, Windows 10, iPhone 12"
                />
              </div>

              <button
                type="submit"
                disabled={submissionStatus.type === 'bug' && submissionStatus.status === 'submitting'}
                className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submissionStatus.type === 'bug' && submissionStatus.status === 'submitting' 
                  ? 'Submitting...' 
                  : 'Submit Bug Report'
                }
              </button>
            </form>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex">
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 rounded-l-lg">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Help & Support</h2>
          </div>
          <nav className="p-4">
            {sections.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveSection(id)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left transition-colors ${
                  activeSection === id
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-sm font-medium">{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {sections.find(section => section.id === activeSection)?.label}
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 p-6 overflow-y-auto">
            {renderSectionContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
